import type { CSSProperties } from 'react';

export const CONTACT_FIELD_KEYS = ['first_name', 'last_name', 'email', 'phone', 'company', 'title'] as const;
export type FormFieldKey = (typeof CONTACT_FIELD_KEYS)[number];
export type FormFieldWidth = 'full' | 'half' | 'third' | 'two-thirds';
/** Presentation belongs to this page section, never to the shared CRM form. */
export interface FormFieldDesign {
  columns?: 1 | 2 | 3;
  options_columns?: 1 | 2 | 3;
  gap?: number;
  padding?: number;
  input_height?: number;
  widths?: Partial<Record<FormFieldKey, FormFieldWidth>>;
  button_width?: 'auto' | 'full';
  button_align?: 'left' | 'center' | 'right';
}

const SPANS: Record<FormFieldWidth, number> = { full: 12, half: 6, third: 4, 'two-thirds': 8 };
const QUERY_LIMIT = 1000;
export const FORM_FIELD_DESIGN_QUERY = 'fields';
export const FORM_FIELD_DESIGN_MESSAGE = 'nla-page-form-design';

/** In the page editor, React portals place the form one frame below the
 * canvas, while its event handlers run in the staff window at the top. */
export function isTrustedFormFieldDesignMessage(
  event: { origin: string; source: unknown; data: unknown },
  context: { origin: string; parent: unknown; top: unknown },
): boolean {
  return event.origin === context.origin && event.source != null
    && (event.source === context.parent || event.source === context.top)
    && !!event.data && typeof event.data === 'object'
    && (event.data as Record<string, unknown>).type === FORM_FIELD_DESIGN_MESSAGE;
}

/** Stored JSON and embed URLs are untrusted. Never turn arbitrary values into CSS. */
export function normalizeFormFieldDesign(raw: unknown): FormFieldDesign | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  const source = raw as Record<string, unknown>;
  const result: FormFieldDesign = {};
  for (const key of ['columns', 'options_columns'] as const) {
    const value = source[key];
    if (value === 1 || value === 2 || value === 3) result[key] = value;
  }
  for (const [key, min, max] of [['gap', 8, 32], ['padding', 12, 48], ['input_height', 44, 64]] as const) {
    const value = source[key];
    if (typeof value === 'number' && Number.isFinite(value) && value >= min && value <= max) result[key] = Math.round(value);
  }
  if (source.button_width === 'auto' || source.button_width === 'full') result.button_width = source.button_width;
  if (source.button_align === 'left' || source.button_align === 'center' || source.button_align === 'right') result.button_align = source.button_align;
  if (source.widths && typeof source.widths === 'object' && !Array.isArray(source.widths)) {
    const widths = source.widths as Record<string, unknown>;
    const safe: FormFieldDesign['widths'] = {};
    for (const key of CONTACT_FIELD_KEYS) {
      const value = widths[key];
      if (typeof value === 'string' && Object.hasOwn(SPANS, value)) safe[key] = value as FormFieldWidth;
    }
    if (Object.keys(safe).length) result.widths = safe;
  }
  return Object.keys(result).length ? result : undefined;
}

/** Stable order keeps equivalent settings from producing different addresses. */
export function formFieldDesignQuery(raw: unknown): string | undefined {
  const design = normalizeFormFieldDesign(raw);
  return design ? JSON.stringify(design) : undefined;
}

export function readFormFieldDesignQuery(raw: string | null): FormFieldDesign | undefined {
  if (!raw || raw.length > QUERY_LIMIT) return undefined;
  try { return normalizeFormFieldDesign(JSON.parse(raw)); } catch { return undefined; }
}

/** Shared variables keep real forms and template stills in step. */
export function formFieldDesignStyle(raw: unknown): CSSProperties | undefined {
  const design = normalizeFormFieldDesign(raw);
  if (!design) return undefined;
  const style: Record<string, string> = {};
  if (design.gap != null) style['--pf-field-gap'] = `${design.gap}px`;
  if (design.padding != null) style['--pf-field-padding'] = `${design.padding}px`;
  if (design.input_height != null) style['--pf-field-height'] = `${design.input_height}px`;
  if (design.options_columns != null) style['--pf-option-columns'] = String(design.options_columns);
  if (design.button_width === 'full') style['--pf-button-width'] = '100%';
  if (design.button_align) {
    style['--pf-button-align'] = { left: 'start', center: 'center', right: 'end' }[design.button_align];
    style['--pf-button-margin-left'] = design.button_align === 'left' ? '0' : 'auto';
    style['--pf-button-margin-right'] = design.button_align === 'right' ? '0' : 'auto';
  }
  return style as CSSProperties;
}

export function formFieldItemStyle(raw: unknown, field: FormFieldKey): CSSProperties | undefined {
  const design = normalizeFormFieldDesign(raw);
  if (!design) return undefined;
  const width = design.widths?.[field];
  const span = width ? SPANS[width] : design.columns ? 12 / design.columns : field === 'first_name' || field === 'last_name' ? 6 : 12;
  return { gridColumn: `span ${span}` };
}
